import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowUpDown } from 'lucide-react';

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

// Zustand Store
import useCanvasStore from '../stores/canvasStore';

import { updateUserSettings } from '../redux/actions/login.action';
import {
    selectIsMinimalView
} from '../redux/selectors/canvas.selectors';
import { CANVAS_SORT_OPTIONS, SORT_FIELD_COUNT } from '../constants/canvasSort';

const FooterBar = ({ fixed = false }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { folderId } = useParams();

    // Read archive status and sort from Zustand
    const archiveStatus = useCanvasStore(s => s.archiveStatus);
    const setArchiveStatus = useCanvasStore(s => s.setArchiveStatus);
    const sortFields = useCanvasStore(s => s.sortFields);
    const sortOrder = useCanvasStore(s => s.sortOrder);
    const setSortFields = useCanvasStore(s => s.setSortFields);
    const bookmarkViewActive = useCanvasStore(s => s.bookmarkViewActive);
    const currentFolder = useCanvasStore(s => s.currentFolder);

    // Read other state from Redux (only for minimal view)
    const isMinimalView = useSelector(selectIsMinimalView);

    // Get visibility flags from footerBarProps (set by page components)
    const footerBarProps = useSelector((state) => state.globalState.footerBarProps);
    const showArchiveToggle = footerBarProps.showArchiveToggle || false;
    const showSortToggle = footerBarProps.showSortToggle || false;
    const showMinimalToggle = footerBarProps.showMinimalToggle || false;

    const handleFilterChange = async (val) => {
        if (!val) return;

        // The URL decides the view (see Home): "/" is the bookmark view, /canvas and /folder/:id the canvas
        if (val === 'bookmarks') {
            navigate('/');
        } else {
            // From the bookmark view, return to the folder the canvas was last on
            const targetFolderId = folderId || currentFolder?.id;
            const newUrl = targetFolderId
                ? `/folder/${targetFolderId}?archiveStatus=${val}`
                : `/canvas?archiveStatus=${val}`;
            navigate(newUrl, { replace: true });

            // Update Zustand store (this will also re-fetch canvas data)
            await setArchiveStatus(val);
        }
    };

    // Handle sort field change for a specific priority position
    const handleSortFieldChange = async (position, newValue) => {
        const currentFields = [...sortFields];
        const oldValue = currentFields[position];

        // Find where the new value currently is
        const swapPosition = currentFields.indexOf(newValue);

        // Swap the values
        currentFields[position] = newValue;
        if (swapPosition !== -1) {
            currentFields[swapPosition] = oldValue;
        }

        await setSortFields(currentFields, sortOrder);
    };

    // Toggle sort order (asc/desc)
    const handleToggleSortOrder = async () => {
        const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
        await setSortFields(sortFields, newOrder);
    };

    const handleMinimalViewChange = (checked) => {
        dispatch(updateUserSettings({ minimal_mode: checked }));
    };

    // Determine current filter value for toggle group
    const currentFilterValue = bookmarkViewActive ? 'bookmarks' : archiveStatus;

    return (
        <div className={cn(
            "bg-card border-t border-border min-h-8 h-auto w-full py-2 px-3 text-left flex items-center overflow-x-hidden z-40",
            fixed && "absolute bottom-0 left-0 right-0"
        )}>
            <div className="flex flex-row justify-between items-center w-full gap-4">
                {/* Left side - Filter groups */}
                <div className="flex items-center gap-1">
                    {/* Bookmarks - Leftmost */}
                    {showArchiveToggle && (
                        <div className="bg-muted/50 rounded-md px-2 py-2">
                            <ToggleGroup
                                type="single"
                                value={currentFilterValue}
                                onValueChange={handleFilterChange}
                                className="gap-0.5"
                            >
                                <ToggleGroupItem
                                    value="bookmarks"
                                    size="sm"
                                    className={cn(
                                        "text-sm capitalize border-none bg-transparent px-2 py-0.5 min-h-0 h-auto",
                                        currentFilterValue === 'bookmarks' ? "text-primary" : "text-muted-foreground",
                                        "hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-transparent"
                                    )}
                                >
                                    Bookmark++
                                </ToggleGroupItem>
                            </ToggleGroup>
                        </div>
                    )}

                    {/* Archive filter group */}
                    {showArchiveToggle && (
                        <div className="bg-muted/50 rounded-md px-2 py-2">
                            <ToggleGroup
                                type="single"
                                value={currentFilterValue}
                                onValueChange={handleFilterChange}
                                className="gap-0.5"
                            >
                                <ToggleGroupItem
                                    value="all"
                                    size="sm"
                                    className={cn(
                                        "text-sm capitalize border-none bg-transparent px-2 py-0.5 min-h-0 h-auto",
                                        currentFilterValue === 'all' ? "text-primary" : "text-muted-foreground",
                                        "hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-transparent"
                                    )}
                                >
                                    All
                                </ToggleGroupItem>
                                <ToggleGroupItem
                                    value="excludeArchive"
                                    size="sm"
                                    className={cn(
                                        "text-sm capitalize border-none bg-transparent px-2 py-0.5 min-h-0 h-auto",
                                        currentFilterValue === 'excludeArchive' ? "text-primary" : "text-muted-foreground",
                                        "hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-transparent"
                                    )}
                                >
                                    Exclude Archive
                                </ToggleGroupItem>
                                <ToggleGroupItem
                                    value="onlyArchive"
                                    size="sm"
                                    className={cn(
                                        "text-sm capitalize border-none bg-transparent px-2 py-0.5 min-h-0 h-auto",
                                        currentFilterValue === 'onlyArchive' ? "text-primary" : "text-muted-foreground",
                                        "hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-transparent"
                                    )}
                                >
                                    Archived only
                                </ToggleGroupItem>
                            </ToggleGroup>
                        </div>
                    )}

                    {/* Sort dropdowns group */}
                    {showSortToggle && (
                        <div className="rounded-md px-2 flex items-center gap-1">
                            <span className="text-xs text-muted-foreground mr-1">Sort:</span>

                            {Array.from({ length: SORT_FIELD_COUNT }, (_, position) => (
                                <React.Fragment key={position}>
                                    {position > 0 && (
                                        <span className="text-xs text-muted-foreground">→</span>
                                    )}
                                    <Select
                                        value={sortFields[position]}
                                        onValueChange={(val) => handleSortFieldChange(position, val)}
                                    >
                                        <SelectTrigger className="h-4 w-auto min-w-[80px] text-xs border-none bg-transparent px-2 py-0 focus:ring-0">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CANVAS_SORT_OPTIONS.map(opt => (
                                                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                                                    {opt.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </React.Fragment>
                            ))}

                            {/* Reverse order button */}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleToggleSortOrder}
                                className={cn(
                                    "h-6 px-1.5 ml-1",
                                    sortOrder === 'asc' ? "text-primary bg-accent" : "text-muted-foreground"
                                )}
                                title={sortOrder === 'asc' ? "Order reversed (click to reset)" : "Reverse sort order"}
                            >
                                <ArrowUpDown className="h-3.5 w-3.5" />
                                <span className="text-xs ml-0.5">Reverse</span>
                            </Button>
                        </div>
                    )}
                </div>

                {/* Right side - Links and minimal toggle */}
                <div className="flex items-center gap-4 ml-auto">
                    <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="text-sm capitalize text-muted-foreground hover:bg-accent hover:text-accent-foreground px-2 py-0.5 h-auto"
                    >
                        <Link to="/debug">Debug</Link>
                    </Button>
                    <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="text-sm capitalize text-muted-foreground hover:bg-accent hover:text-accent-foreground px-2 py-0.5 h-auto"
                    >
                        <Link to="/visual-changelog">Visual Changelog</Link>
                    </Button>

                    {showMinimalToggle && (
                        <div className="flex items-center gap-1.5">
                            <Checkbox
                                id="minimal-view"
                                checked={isMinimalView}
                                onCheckedChange={handleMinimalViewChange}
                                className="border-input data-[state=checked]:bg-primary data-[state=checked]:border-primary h-4 w-4"
                            />
                            <Label
                                htmlFor="minimal-view"
                                className="text-sm text-muted-foreground cursor-pointer whitespace-nowrap"
                            >
                                Minimal View
                            </Label>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FooterBar;
